import type { PresentationAction } from './brain-team-presentation-plan';

/** Local renderer snapshot. request_id and team/thread ownership belong to Ask.
 * Only the documented wire fields are sent to the responder; owner_key stays local. */
export type AtlasPresentationSnapshot = {
  schema_version: 1;
  owner_key: string;
  view_revision: number;
  interaction_revision: number;
  mode: 'overview' | 'guided' | 'exploring' | 'compare';
  selected_target_ids: string[];
  comparison_target_ids?: [string, string];
  visible_layers: Array<{ kind: 'register' | 'experience'; id: string }>;
  approved_story?: { story_id: string; revision: number };
  scene_id?: string;
  dataset_version?: string;
  resume_checkpoint_id?: string;
  camera?: { center: [number, number]; zoom: number; bearing?: number; pitch?: number };
};

export type AtlasPresentationViewContext = Omit<AtlasPresentationSnapshot, 'owner_key'> & {
  request_id: string;
};

/** The host checks request/turn/owner/evidence first. The renderer checks again
 * against its committed state before applying the whole command atomically. */
export type AtlasPresentationCommand = {
  plan_id: string;
  owner_key: string;
  base_view_revision: number;
  base_interaction_revision: number;
  approved_story?: { story_id: string; revision: number };
  dataset_version?: string;
  action: PresentationAction;
  /** Deliberate replay uses freshly captured revisions; never bypass ownership. */
  explicit?: boolean;
};

export type AtlasPresentationController = {
  read: () => AtlasPresentationSnapshot | null;
  captureForAsk: () => AtlasPresentationSnapshot | null;
  apply: (command: AtlasPresentationCommand) => boolean;
};

export const ATLAS_PRESENTATION_CAPABILITIES = {
  schema_versions: [1] as [1],
  actions: ['show', 'compare', 'explain', 'resume'] as Array<PresentationAction['kind']>,
};

export function atlasPresentationViewContext(
  snapshot: AtlasPresentationSnapshot,
  requestId: string,
): AtlasPresentationViewContext {
  const { owner_key: _owner, ...wire } = snapshot;
  return { ...wire, request_id: requestId };
}
